import React, { useEffect, useState, useRef } from 'react';
import isUndefined from 'lodash/isUndefined';
import warning from 'warning';
import useLayoutEffect from './useIsomorphicLayoutEffect';
import FormControl from '../controllers/FormControl';
import { AdapterContext, FormContext, FormApiContext, FormStateContext } from '../Context';
import useContextControl from './useContextControl';
import useIsEqualMemoize from './useIsEqualMemoize';
import { JSON_SCHEMA_KEYWORDS } from '../constants';

const parseSchema = (schema, props) => {
  const parsedSchema = { ...schema } ?? {};

  JSON_SCHEMA_KEYWORDS.forEach((keyword) => {
    if (!isUndefined(props[keyword])) {
      parsedSchema[keyword] = props[keyword];
    }
  });

  return parsedSchema;
};

export default function useForm({
  components,
  focusOnInvalid,
  formApiRef,
  formContextRef,
  formStateRef: userFormStateRef,
  i18n,
  initialValues,
  noValidate,
  onAfterValidate,
  onBeforeValidate,
  onBlur,
  onChange,
  onFocus,
  onInvalid,
  onSubmit,
  onValid,
  removeAdditional,
  removeEmptyValues,
  schema,
  validate,
  validateFields,
  validateOnBlur,
  validateOnChange,
  validator,
  values,
  ...props
}) {
  const parsedSchema = parseSchema(schema, props);
  const formStateRef = useRef();
  const context = useContextControl(
    {
      components,
      focusOnInvalid,
      i18n,
      initialValues: initialValues ?? values,
      noValidate,
      removeAdditional,
      removeEmptyValues,
      schema: parsedSchema,
      validateOnBlur,
      validateOnChange,
      validator,
    },
    formStateRef
  );
  const isUnmounted = useRef(false);

  useEffect(() => {
    warning(!values, '`values` is deprecated, please use `initialValues` instead');
  }, [values]);

  const [formControl] = useState(() =>
    new FormControl(props).withContext({
      ...context,
      ...context.stateRef.current,
    })
  );

  useEffect(() => {
    formControl.setProps(props);
  }, [useIsEqualMemoize(props)]);

  const {
    actions: formApi,
    defaultHandler,
    getProps,
    getUserProps,
    properties,
    subscribe,
    subscribeContext,
    unsubscribe,
    unsubscribeContext,
  } = formControl;

  const [formState, setFormState] = useState(properties);

  formStateRef.current = formState;

  useLayoutEffect(() => {
    const stateSubscription = (state) => {
      const { properties: nextFormState } = formControl.withContext({
        ...context,
        ...state,
      });

      setFormState(nextFormState);
    };

    subscribeContext();

    context.subscribe('state', stateSubscription);

    return () => {
      unsubscribeContext();

      context.unsubscribe('state', stateSubscription);

      isUnmounted.current = true;
    };
  }, []);

  useLayoutEffect(() => {
    if (onAfterValidate) subscribe('aftervalidate', onAfterValidate);
    if (onBeforeValidate) subscribe('beforevalidate', onBeforeValidate);
    if (onBlur) subscribe('blur', onBlur);
    if (onChange) subscribe('change', onChange);
    if (onFocus) subscribe('focus', onFocus);
    if (onInvalid) subscribe('invalid', onInvalid);
    if (onSubmit) subscribe('submit', onSubmit);
    if (onValid) subscribe('valid', onValid);
    if (validate) subscribe('validate', validate);
    if (validateFields) subscribe('validateFields', validateFields);

    return () => {
      if (onAfterValidate) unsubscribe('aftervalidate', onAfterValidate);
      if (onBeforeValidate) unsubscribe('beforevalidate', onBeforeValidate);
      if (onBlur) unsubscribe('blur', onBlur);
      if (onChange) unsubscribe('change', onChange);
      if (onFocus) unsubscribe('focus', onFocus);
      if (onInvalid) unsubscribe('invalid', onInvalid);
      if (onSubmit) unsubscribe('submit', onSubmit);
      if (onValid) unsubscribe('valid', onValid);
      if (validate) unsubscribe('validate', validate);
      if (validateFields) unsubscribe('validateFields', validateFields);
    };
  }, [onAfterValidate, onBeforeValidate, onBlur, onChange, onFocus, onInvalid, onSubmit, onValid]);

  const adapter = context.getAdapter();

  const render = (children) => (
    <AdapterContext.Provider value={adapter}>
      <FormContext.Provider value={context}>
        <FormApiContext.Provider value={formApi}>
          <FormStateContext.Provider value={formState}>{children}</FormStateContext.Provider>
        </FormApiContext.Provider>
      </FormContext.Provider>
    </AdapterContext.Provider>
  );

  const formContext = {
    ...formApi,
    ...formState,
  };

  /* eslint-disable no-param-reassign */
  if (formApiRef) {
    formApiRef.current = formApi;
  }

  if (formContextRef) {
    formContextRef.current = formContext;
  }

  if (userFormStateRef) {
    userFormStateRef.current = formState;
  }

  return {
    adapter,
    defaultHandler,
    formApi,
    formContext,
    formState,
    formProps: getProps(),
    render,
    userProps: getUserProps(),
  };
}
